import * as React from "react";
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from "@mui/material";
import { useNotification } from "../../context/notification-provider";
import LoadingScreen from "../progress/loading-screen";
import FilterTypeForm, {
  FilterTypeFormState,
  FilterTypeFormValues,
  FormFieldValue,
  validateFilterType,
} from "./filter-type-form";
import useFilterShapes from "./use-filter-shapes";
import { updateFilterType } from "./service";
import {
  FilterDefaultVisibility,
  FilterSelectionMode,
  FilterShape,
  FilterSource,
  FilterTypeDetail,
} from "./types";

const FilterTypeEditDialog = ({
  selectedFilterType,
  open,
  onClose,
}: {
  selectedFilterType: FilterTypeDetail;
  open: boolean;
  onClose: () => void;
}) => {
  const { data: shapes, error: loadingError, isLoading } = useFilterShapes();
  const { showNotification } = useNotification();
  const [isSubmitting, setIsSubmitting] = React.useState(false);

  const [formState, setFormState] = React.useState<FilterTypeFormState>({
    values: { ...selectedFilterType },
    errors: {},
  });
  const formValues = formState.values;

  const handleFieldChange = React.useCallback(
    (name: keyof FilterTypeFormValues, value: FormFieldValue) => {
      setFormState((previousState) => ({
        ...previousState,
        values: { ...previousState.values, [name]: value },
      }));
    },
    [],
  );

  const handleSubmit = React.useCallback(async () => {
    const { issues } = validateFilterType(formValues, true);

    if (issues.length > 0) {
      setFormState((previousState) => ({
        ...previousState,
        errors: Object.fromEntries(
          issues.map((issue) => [issue.path[0], issue.message]),
        ),
      }));
      return;
    }

    setFormState((previousState) => ({ ...previousState, errors: {} }));
    setIsSubmitting(true);

    try {
      await updateFilterType({
        id: selectedFilterType.id,
        label: formValues.label ?? "",
        shapeId: formValues.shapeId as FilterShape,
        source: formValues.source as FilterSource,
        selectionMode: formValues.selectionMode ?? FilterSelectionMode.Multi,
        defaultVisibility:
          formValues.defaultVisibility ?? FilterDefaultVisibility.VisibleToAll,
        sortOrder: formValues.sortOrder ?? 0,
        targetField: formValues.targetField || null,
        targetColumn: formValues.targetColumn || null,
        isEnabled: formValues.isEnabled ?? true,
        isDropdown: formValues.isDropdown ?? false,
        isSystem: formValues.isSystem ?? selectedFilterType.isSystem,
      });

      showNotification({
        message: "Filter type updated successfully.",
        severity: "success",
        duration: 3000,
      });

      onClose();
    } catch (updateError) {
      showNotification({
        message: `Failed to update filter type. Reason: ${(updateError as Error).message}`,
        severity: "error",
        duration: 3000,
      });
    } finally {
      setIsSubmitting(false);
    }
  }, [
    formValues,
    selectedFilterType.id,
    selectedFilterType.isSystem,
    showNotification,
    onClose,
  ]);

  return (
    <Dialog open={open} onClose={() => onClose()} maxWidth="md" fullWidth>
      <DialogTitle sx={{ borderBottom: 2, mb: 2, borderColor: "divider" }}>
        Edit Filter Type
      </DialogTitle>
      {isLoading ? (
        <LoadingScreen />
      ) : (
        <DialogContent sx={{ pb: 0 }}>
          {loadingError ? (
            <Alert severity="error">Unable to load filter shapes.</Alert>
          ) : (
            <FilterTypeForm
              formState={formState}
              shapes={shapes || []}
              isEdit
              onFieldChange={handleFieldChange}
              onSubmit={handleSubmit}
            />
          )}
        </DialogContent>
      )}
      <DialogActions>
        <Button onClick={() => onClose()}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          loading={isSubmitting}
          disabled={isSubmitting}
        >
          Update filter type
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default FilterTypeEditDialog;
